import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import type { AuthUser, AuthTokens, LoginPayload, RegisterPayload, AuthResponse } from './types';
import { authApi, setTokenAccessors } from '../lib/api-client';
import { analytics } from '../lib/analytics';

// --- localStorage helpers ---

const STORAGE_KEY_ACCESS = 'sf.auth.accessToken';
const STORAGE_KEY_REFRESH = 'sf.auth.refreshToken';

function readTokensFromStorage(): AuthTokens | null {
  const accessToken = localStorage.getItem(STORAGE_KEY_ACCESS);
  const refreshToken = localStorage.getItem(STORAGE_KEY_REFRESH);
  if (accessToken && refreshToken) return { accessToken, refreshToken };
  return null;
}

function persistTokens(tokens: AuthTokens) {
  localStorage.setItem(STORAGE_KEY_ACCESS, tokens.accessToken);
  localStorage.setItem(STORAGE_KEY_REFRESH, tokens.refreshToken);
}

function clearTokens() {
  localStorage.removeItem(STORAGE_KEY_ACCESS);
  localStorage.removeItem(STORAGE_KEY_REFRESH);
}

// --- Context ---

type AuthContextValue = {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (payload: LoginPayload) => Promise<void>;
  register: (payload: RegisterPayload) => Promise<void>;
  googleAuth: (idToken: string) => Promise<void>;
  githubAuth: (code: string) => Promise<void>;
  logout: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [tokens, setTokens] = useState<AuthTokens | null>(readTokensFromStorage);
  const [isLoading, setIsLoading] = useState(() => Boolean(readTokensFromStorage()));
  const tokensRef = useRef(tokens);
  tokensRef.current = tokens;

  // Wire up api-client token accessors
  useEffect(() => {
    setTokenAccessors(
      () => tokensRef.current,
      (newTokens) => {
        setTokens(newTokens);
        persistTokens(newTokens);
      },
      () => {
        setUser(null);
        setTokens(null);
        clearTokens();
      },
    );
  }, []);

  // Validate session on mount
  useEffect(() => {
    if (!tokens) {
      setIsLoading(false);
      return;
    }

    let mounted = true;

    authApi
      .me()
      .then((data) => {
        if (mounted) setUser(data);
      })
      .catch(() => {
        if (mounted) {
          setUser(null);
          setTokens(null);
          clearTokens();
        }
      })
      .finally(() => {
        if (mounted) setIsLoading(false);
      });

    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAuthResponse = useCallback((response: AuthResponse) => {
    const authUser: AuthUser = { ...response.user, workspace: null };
    setUser(authUser);
    const newTokens = { accessToken: response.accessToken, refreshToken: response.refreshToken };
    setTokens(newTokens);
    persistTokens(newTokens);
    // Update ref synchronously so subsequent API calls use the new tokens
    tokensRef.current = newTokens;
    // Fetch full profile (includes workspace) in the background
    authApi.me().then(setUser).catch(() => {});
  }, []);

  const login = useCallback(
    async (payload: LoginPayload) => {
      const response = await authApi.login(payload);
      handleAuthResponse(response);
      analytics.login('email');
    },
    [handleAuthResponse],
  );

  const register = useCallback(
    async (payload: RegisterPayload) => {
      const response = await authApi.register(payload);
      handleAuthResponse(response);
      analytics.signUp('email');
    },
    [handleAuthResponse],
  );

  const googleAuth = useCallback(
    async (idToken: string) => {
      const response = await authApi.google(idToken);
      handleAuthResponse(response);
      analytics.login('google');
    },
    [handleAuthResponse],
  );

  const githubAuth = useCallback(
    async (code: string) => {
      const response = await authApi.github(code);
      handleAuthResponse(response);
      analytics.login('github');
    },
    [handleAuthResponse],
  );

  const logout = useCallback(async () => {
    const current = tokensRef.current;
    if (current?.refreshToken) {
      try {
        await authApi.logout(current.refreshToken);
      } catch {
        /* ignore */
      }
    }
    setUser(null);
    setTokens(null);
    clearTokens();
    analytics.logout();
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: Boolean(user),
        isLoading,
        login,
        register,
        googleAuth,
        githubAuth,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
